import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { User, Shield, ShieldCheck, Truck, CreditCard, Store, Bell, Lock, BadgeCheck, Award, Globe, HelpCircle, Trash2, ChevronRight, ArrowLeft, Info, Download, Cookie, KeyRound, Smartphone, LogOut, Mail, Rocket, Plane, Wallet, Heart, Package, Tag, Star, FileText, AlertTriangle, Gift } from "lucide-react";
import { toast } from "sonner";
import { api, errText, imgUrl, formatDate, formatPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { BadgeChip } from "../components/BadgeChip";

const SECTIONS = [
  { id: "profil", label: "Profil", icon: User },
  { id: "konto", label: "Konto", icon: Mail },
  { id: "sicherheit", label: "Sicherheit", icon: Shield },
  { id: "versand", label: "Versand", icon: Truck },
  { id: "zahlungen", label: "Zahlungen & Auszahlungen", icon: CreditCard },
  { id: "verkaeufer", label: "Verkäufer", icon: Store },
  { id: "steuern", label: "Steuern & DAC7", icon: FileText, hidden: true },
  { id: "werbung", label: "Werbung & Boosts", icon: Rocket },
  { id: "benachrichtigungen", label: "Benachrichtigungen", icon: Bell },
  { id: "datenschutz", label: "Datenschutz & Privatsphäre", icon: Lock },
  { id: "verifizierung", label: "Verifizierung", icon: BadgeCheck },
  { id: "abzeichen", label: "Meine Abzeichen", icon: Award },
  { id: "sprache", label: "Sprache & Region", icon: Globe },
  { id: "hilfe", label: "Hilfe", icon: HelpCircle },
  { id: "loeschen", label: "Konto löschen", icon: Trash2, danger: true },
];

const QUICK_LINKS = [
  { to: "/geldbeutel", label: "Geldbeutel", icon: Wallet },
  { to: "/gewinnspiel", label: "Gewinnspiel", icon: Gift },
  { to: "/favoriten", label: "Favoriten", icon: Heart },
  { to: "/anfragen", label: "Support-Anfragen", icon: HelpCircle },
  { to: "/bestellungen", label: "Meine Bestellungen", icon: Package },
  { to: "/nachrichten", label: "Angebote", icon: Tag },
  { to: "/benachrichtigungen", label: "Benachrichtigungen", icon: Bell },
];

const card = "bg-white border border-border rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]";
const input = "w-full rounded-xl border border-[#DADADA] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent transition-colors";
const lbl = "text-xs font-semibold block mb-1.5";
const btnPrimary = "bg-foreground text-white text-sm font-bold px-6 py-2.5 rounded-full hover:opacity-90 active:scale-[0.98] transition-transform disabled:opacity-50";
const btnSecondary = "border border-border bg-white text-sm font-bold px-5 py-2.5 rounded-full hover:bg-secondary transition-colors";

function InfoCard({ title, text, action }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-4 flex gap-3">
      <span className="w-8 h-8 rounded-full bg-accent/10 text-accent flex items-center justify-center shrink-0"><Info className="w-4 h-4" /></span>
      <div>
        <p className="text-sm font-bold">{title}</p>
        <p className="text-sm text-muted-foreground mt-0.5">{text}</p>
        {action}
      </div>
    </div>
  );
}

function Row({ label, desc, children, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-border/60 last:border-0" data-testid={testid}>
      <div>
        <p className="text-sm font-medium">{label}</p>
        {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
      </div>
      {children}
    </div>
  );
}

function useSaver(refresh) {
  const [busy, setBusy] = useState(false);
  const save = async (payload, msg = "Gespeichert") => {
    setBusy(true);
    try {
      await api.put("/auth/profile", payload);
      await refresh();
      toast.success(msg);
    } catch (e) {
      toast.error("Änderungen konnten nicht gespeichert werden.", { description: errText(e) });
    } finally { setBusy(false); }
  };
  return [save, busy];
}

/* ================= Sections ================= */

function ProfilSection({ user, refresh }) {
  const [f, setF] = useState({ name: user.name || "", username: user.username || "", bio: user.bio || "", country: user.country || "Deutschland" });
  const [save, busy] = useSaver(refresh);
  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd);
      await save({ avatar_url: imgUrl(data.image_id, "thumb") }, "Profilbild aktualisiert");
    } catch (err) { toast.error(errText(err)); }
  };
  return (
    <div className="space-y-4">
      <div className={card}>
        <p className="font-bold text-sm mb-4">Profilbild</p>
        <div className="flex items-center gap-4">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-16 h-16 rounded-full object-cover border border-border" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold">{user.username?.[0]?.toUpperCase()}</div>
          )}
          <label className={`${btnSecondary} cursor-pointer`}>
            Profilbild ändern
            <input type="file" data-testid="avatar-upload-input" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadAvatar} />
          </label>
        </div>
      </div>
      <div className={card}>
        <p className="font-bold text-sm mb-4">Profil-Details</p>
        <div className="space-y-4">
          <div><label className={lbl}>Benutzername</label><input data-testid="settings-username" className={input} value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></div>
          <div><label className={lbl}>Vollständiger Name</label><input data-testid="settings-name" className={input} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><label className={lbl}>Über mich</label><textarea data-testid="settings-bio" className={`${input} min-h-24`} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} placeholder="Erzähle etwas über dich…" /></div>
          <div><label className={lbl}>Land</label>
            <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v })}>
              <SelectTrigger data-testid="settings-country" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Deutschland">Deutschland</SelectItem><SelectItem value="Österreich">Österreich</SelectItem><SelectItem value="Schweiz">Schweiz</SelectItem></SelectContent>
            </Select>
          </div>
          <button data-testid="settings-save-profile" disabled={busy} onClick={() => save(f)} className={btnPrimary}>Änderungen speichern</button>
        </div>
      </div>
      <div className={card} data-testid="vacation-card">
        <p className="font-bold text-sm mb-1 flex items-center gap-2"><Plane className="w-4 h-4 text-accent" /> Urlaubsmodus</p>
        <Row label="Urlaubsmodus aktivieren" desc="Alle deine aktiven Artikel werden pausiert und sind für Käufer nicht sichtbar. Beim Deaktivieren gehen sie automatisch wieder online." testid="vacation-row">
          <Switch data-testid="vacation-switch" checked={!!user.vacation_mode}
            onCheckedChange={(v) => save({ vacation_mode: v }, v ? "Urlaubsmodus aktiviert — deine Artikel sind pausiert" : "Urlaubsmodus deaktiviert — deine Artikel sind wieder online")} />
        </Row>
      </div>
    </div>
  );
}

function KontoSection({ user, refresh }) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [em, setEm] = useState({ new_email: "", password: "" });
  const [phone, setPhone] = useState(user.phone || "");
  const [birth, setBirth] = useState(user.birth_date || "");
  const [save, busy] = useSaver(refresh);
  const changeEmail = async () => {
    try {
      await api.post("/auth/change-email", em);
      await refresh();
      setEmailOpen(false);
      toast.success("E-Mail geändert", { description: "Bitte bestätige deine neue E-Mail-Adresse." });
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-4">
      <div className={card}>
        <p className="font-bold text-sm mb-1">Konto-Einstellungen</p>
        <Row label="E-Mail-Adresse" desc={user.email} testid="konto-email-row">
          <button data-testid="change-email-btn" onClick={() => setEmailOpen(true)} className={btnSecondary}>E-Mail ändern</button>
        </Row>
        <Row label="Kontostatus" testid="konto-status-row">
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${user.email_verified ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
            {user.email_verified ? "Verifiziert" : "Nicht verifiziert"}
          </span>
        </Row>
        <div className="pt-3 space-y-4">
          <div><label className={lbl}>Telefonnummer</label>
            <div className="flex gap-2">
              <input data-testid="settings-phone" className={input} placeholder="Telefonnummer hinzufügen" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <button data-testid="save-phone-btn" disabled={busy} onClick={() => save({ phone })} className={btnSecondary}>Speichern</button>
            </div>
          </div>
          <div><label className={lbl}>Geburtsdatum (optional)</label>
            <div className="flex gap-2">
              <input data-testid="settings-birthdate" type="date" className={input} value={birth} onChange={(e) => setBirth(e.target.value)} />
              <button data-testid="save-birthdate-btn" disabled={busy} onClick={() => save({ birth_date: birth })} className={btnSecondary}>Speichern</button>
            </div>
          </div>
        </div>
      </div>
      <InfoCard title="Telefonnummer hinzufügen" text="Eine bestätigte Telefonnummer erhöht die Sicherheit deines Kontos." />
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>E-Mail-Adresse ändern</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="new-email-input" className={input} type="email" placeholder="Neue E-Mail-Adresse" value={em.new_email} onChange={(e) => setEm({ ...em, new_email: e.target.value })} />
            <input data-testid="email-password-input" className={input} type="password" placeholder="Aktuelles Passwort" value={em.password} onChange={(e) => setEm({ ...em, password: e.target.value })} />
            <button data-testid="confirm-change-email" onClick={changeEmail} disabled={!em.new_email || !em.password} className={`${btnPrimary} w-full`}>E-Mail ändern</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SicherheitSection() {
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState(null);
  useEffect(() => { api.get("/auth/sessions").then(({ data }) => setSessions(data)).catch(() => setSessions([])); }, []);
  const changePw = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Passwort geändert");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const logoutAll = async () => {
    try {
      await api.post("/auth/logout-all");
      const { data } = await api.get("/auth/sessions");
      setSessions(data);
      toast.success("Von allen anderen Geräten abgemeldet");
    } catch (e) { toast.error(errText(e)); }
  };
  const deviceName = (ua = "") => {
    if (/iphone|ipad/i.test(ua)) return "iPhone / iPad";
    if (/android/i.test(ua)) return "Android";
    if (/mac/i.test(ua)) return "Mac";
    if (/windows/i.test(ua)) return "Windows";
    if (/linux/i.test(ua)) return "Linux";
    return "Unbekanntes Gerät";
  };
  const browserName = (ua = "") => {
    if (/edg/i.test(ua)) return "Edge";
    if (/chrome/i.test(ua)) return "Chrome";
    if (/safari/i.test(ua) && !/chrome/i.test(ua)) return "Safari";
    if (/firefox/i.test(ua)) return "Firefox";
    return "Browser";
  };
  return (
    <div className="space-y-4">
      <div className={card}>
        <p className="font-bold text-sm mb-4 flex items-center gap-2"><KeyRound className="w-4 h-4 text-accent" /> Passwort ändern</p>
        <div className="space-y-3">
          <input data-testid="current-password-input" className={input} type="password" placeholder="Aktuelles Passwort" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
          <input data-testid="new-password-input" className={input} type="password" placeholder="Neues Passwort (min. 8 Zeichen)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
          <button data-testid="change-password-btn" onClick={changePw} disabled={busy || pw.new_password.length < 8 || !pw.current_password} className={btnPrimary}>Passwort speichern</button>
        </div>
      </div>
      <div className={card}>
        <Row label="Zwei-Faktor-Authentifizierung" desc="Bald verfügbar — zusätzliche Sicherheit für dein Konto." testid="twofa-row">
          <Switch data-testid="twofa-switch" disabled checked={false} />
        </Row>
      </div>
      <div className={card}>
        <p className="font-bold text-sm mb-2 flex items-center gap-2"><Smartphone className="w-4 h-4 text-accent" /> Angemeldete Geräte & Login-Aktivität</p>
        {sessions === null && <div className="h-16 bg-muted rounded-xl animate-pulse" />}
        {sessions?.length === 0 && <p className="text-sm text-muted-foreground py-2">Keine aufgezeichneten Anmeldungen.</p>}
        <div className="space-y-2" data-testid="sessions-list">
          {sessions?.map((s) => (
            <div key={s.id} className="flex items-center justify-between border border-border/70 rounded-xl px-3.5 py-2.5">
              <div>
                <p className="text-sm font-medium">{deviceName(s.user_agent)} · {browserName(s.user_agent)} {s.current && <span className="text-[10px] font-bold text-accent ml-1">Dieses Gerät</span>}</p>
                <p className="text-xs text-muted-foreground">IP {s.ip} · {formatDate(s.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
        <button data-testid="logout-all-btn" onClick={logoutAll} className={`${btnSecondary} mt-4 flex items-center gap-2`}><LogOut className="w-4 h-4" /> Von allen anderen Geräten abmelden</button>
      </div>
    </div>
  );
}

const SHIP_METHODS = [
  { id: "STANDARD", label: "Standardversand (versichert)" },
  { id: "DHL", label: "DHL" }, { id: "HERMES", label: "Hermes" }, { id: "DPD", label: "DPD" },
  { id: "GLS", label: "GLS" }, { id: "POST", label: "Deutsche Post" }, { id: "ABHOLUNG", label: "Abholung" },
];

function VersandSection({ user, refresh }) {
  const [addr, setAddr] = useState(user.address || { street: "", zip: "", city: "", country: "Deutschland" });
  const [methods, setMethods] = useState(user.shipping_settings?.methods || { STANDARD: true });
  const [save, busy] = useSaver(refresh);
  return (
    <div className="space-y-4">
      <div className={card}>
        <p className="font-bold text-sm mb-4">Deine Versandadresse</p>
        <div className="space-y-3">
          <input data-testid="ship-street" className={input} placeholder="Straße und Hausnummer" value={addr.street || ""} onChange={(e) => setAddr({ ...addr, street: e.target.value })} />
          <div className="grid grid-cols-3 gap-3">
            <input data-testid="ship-zip" className={input} placeholder="PLZ" value={addr.zip || ""} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} />
            <input data-testid="ship-city" className={`${input} col-span-2`} placeholder="Ort" value={addr.city || ""} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
          </div>
          <button data-testid="save-address-btn" disabled={busy} onClick={() => save({ address: addr })} className={btnPrimary}>{user.address ? "Adresse speichern" : "Adresse hinzufügen"}</button>
        </div>
      </div>
      <div className={card}>
        <p className="font-bold text-sm mb-1">Versand als Verkäufer</p>
        <p className="text-xs text-muted-foreground mb-2">Diese Versandmethoden stehen dir beim Einstellen neuer Artikel zur Verfügung.</p>
        {SHIP_METHODS.map((m) => (
          <Row key={m.id} label={m.label} testid={`ship-method-${m.id}`}>
            <Switch data-testid={`ship-method-switch-${m.id}`} checked={methods[m.id] !== false && (m.id === "STANDARD" ? true : !!methods[m.id])}
              disabled={m.id === "STANDARD"}
              onCheckedChange={(v) => {
                const next = { ...methods, [m.id]: v };
                setMethods(next);
                save({ shipping_settings: { ...(user.shipping_settings || {}), methods: next } });
              }} />
          </Row>
        ))}
      </div>
    </div>
  );
}

function ZahlungenSection({ user }) {
  const nav = useNavigate();
  const seller = user.seller || {};
  const verified = seller.payouts_enabled;
  const [bal, setBal] = useState(null);
  useEffect(() => { api.get("/seller/balance").then(({ data }) => setBal(data)).catch(() => setBal(false)); }, []);
  return (
    <div className="space-y-4">
      <div className={card} data-testid="balance-card">
        <p className="font-bold text-sm mb-2 flex items-center gap-2"><Wallet className="w-4 h-4 text-accent" /> Dein Guthaben</p>
        {bal === null && <div className="h-12 bg-muted rounded-xl animate-pulse" />}
        {bal === false && <p className="text-sm text-muted-foreground">Dein Guthaben konnte gerade nicht abgerufen werden. Bitte versuche es später erneut.</p>}
        {bal && bal.connected === false && (
          <p className="text-sm text-muted-foreground">Noch kein Stripe-Konto verbunden. Sobald du als Verkäufer verifiziert bist, siehst du hier dein echtes Guthaben.</p>
        )}
        {bal && bal.connected && (
          <div className="flex flex-wrap gap-6 mt-1">
            <div>
              <p className="text-2xl font-bold" data-testid="balance-available">{formatPrice(bal.available)}</p>
              <p className="text-xs text-muted-foreground">Verfügbar</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-muted-foreground" data-testid="balance-pending">{formatPrice(bal.pending)}</p>
              <p className="text-xs text-muted-foreground">Unterwegs / ausstehend</p>
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3">Dein Guthaben liegt sicher bei unserem Zahlungsdienstleister Stripe — KLEDIO verwahrt kein Geld. Auszahlungen erfolgen automatisch auf dein Bankkonto.</p>
      </div>
      <div className={card}>
        <p className="font-bold text-sm mb-2">Zahlungsmethoden (Käufer)</p>
        <p className="text-sm text-muted-foreground">Deine Zahlungsdaten werden sicher und verschlüsselt von unserem Zahlungsdienstleister Stripe verarbeitet. KLEDIO speichert keine Kartendaten. Beim Bezahlen wählst du deine Zahlungsart (z. B. Kredit-/Debitkarte, Apple Pay oder Google Pay) direkt auf der sicheren Bezahlseite aus.</p>
      </div>
      <div className={card} data-testid="payout-status-card">
        <p className="font-bold text-sm mb-2">Auszahlungen (Verkäufer)</p>
        <div className="flex items-center gap-2 mt-1">
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${verified ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`} data-testid="payout-status-badge">
            {verified ? "✓ Verifiziert — Auszahlungen aktiv" : "Verifizierung erforderlich"}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mt-2.5">
          {verified
            ? "Dein Verkäuferkonto ist verifiziert. Verkaufserlöse werden nach Ablauf der Schutzfrist automatisch ausgezahlt."
            : "Um Auszahlungen zu erhalten, schließe die Stripe-Verifizierung im Verkäuferbereich ab. Deine Bankdaten werden ausschließlich von Stripe verwaltet und niemals bei KLEDIO gespeichert oder angezeigt."}
        </p>
        <button data-testid="goto-seller-dashboard" onClick={() => nav("/verkaeufer")} className={`${btnPrimary} mt-4`}>
          {verified ? "Zum Verkäuferbereich" : "Stripe-Verifizierung abschließen"}
        </button>
      </div>
    </div>
  );
}

function VerkaeuferSection({ user, refresh }) {
  const [s, setS] = useState({ negotiable_default: true, use_min_price: false, auto_reserve: true, default_shipping: "STANDARD", ...(user.seller_settings || {}) });
  const [save] = useSaver(refresh);
  const upd = (k, v) => {
    const next = { ...s, [k]: v };
    setS(next);
    save({ seller_settings: next });
  };
  return (
    <div className="space-y-4">
      <div className={card}>
        <p className="font-bold text-sm mb-1">Verkäufer-Einstellungen</p>
        <Row label="Verhandlungen erlauben" desc="Neue Artikel sind standardmäßig verhandelbar." testid="seller-negotiable-row">
          <Switch data-testid="seller-negotiable-switch" checked={s.negotiable_default !== false} onCheckedChange={(v) => upd("negotiable_default", v)} />
        </Row>
        <Row label="Mindestpreis verwenden" desc="Angebote unter deinem versteckten Mindestpreis werden automatisch abgelehnt." testid="seller-minprice-row">
          <Switch data-testid="seller-minprice-switch" checked={!!s.use_min_price} onCheckedChange={(v) => upd("use_min_price", v)} />
        </Row>
        <Row label="Automatische Artikelreservierung" desc="Artikel wird bei angenommenem Angebot 30 Min. für den Käufer reserviert." testid="seller-reserve-row">
          <Switch data-testid="seller-reserve-switch" checked={s.auto_reserve !== false} onCheckedChange={(v) => upd("auto_reserve", v)} />
        </Row>
        <div className="pt-3">
          <label className={lbl}>Standardversand</label>
          <Select value={s.default_shipping} onValueChange={(v) => upd("default_shipping", v)}>
            <SelectTrigger data-testid="seller-default-shipping" className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{SHIP_METHODS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <InfoCard title="Schnellangebote" text="Käufer können dir Schnellangebote mit 10 %, 15 % oder 20 % Rabatt senden. Diese Stufen werden von KLEDIO zentral verwaltet." />
    </div>
  );
}

function SteuernSection() {
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/tax/profile").then(({ data }) => {
    setData(data);
    const d = data.data || {};
    setF({ country: d.country || "DE", account_type: d.account_type || "PRIVATE",
      first_name: d.first_name || "", last_name: d.last_name || "", birth_date: d.birth_date || "",
      company_name: d.company_name || "", street: d.street || "", zip_code: d.zip_code || "",
      city: d.city || "", tin: d.tin || "", vat_id: d.vat_id || "", business_reg_no: d.business_reg_no || "" });
  }).catch(() => setData(false));
  useEffect(() => { load(); }, []);
  if (data === null || !f) return <div className="h-40 bg-muted rounded-2xl animate-pulse" />;
  if (data === false) return <p className="text-sm text-muted-foreground">Steuerdaten konnten nicht geladen werden.</p>;
  const rules = data.country_rules[f.country];
  const STATUS = {
    MISSING: ["Angaben fehlen", "bg-amber-50 text-amber-800 border-amber-300"],
    SUBMITTED: ["Eingereicht — wird geprüft", "bg-blue-50 text-blue-800 border-blue-300"],
    VERIFIED: ["Geprüft ✓", "bg-green-50 text-green-800 border-green-300"],
    REJECTED: ["Bitte korrigieren", "bg-red-50 text-red-800 border-red-300"],
  };
  const [stLabel, stCls] = STATUS[data.status] || STATUS.MISSING;
  const inp = "w-full rounded-xl border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
  const lb = "text-xs font-semibold block mb-1.5";
  const setV = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f };
      ["birth_date", "company_name", "tin", "vat_id", "business_reg_no"].forEach((k) => { if (!payload[k]) payload[k] = null; });
      const { data: d } = await api.put("/tax/profile", payload);
      setData(d);
      toast.success("Steuerangaben gespeichert", { description: "Deine Angaben werden geprüft." });
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="tax-section">
      <div className={`border rounded-2xl p-4 text-sm font-medium ${stCls}`} data-testid="tax-status-banner">
        Status deiner Steuerangaben: <b>{stLabel}</b>
        {data.review_note && data.status === "REJECTED" && <p className="mt-1 font-normal">Hinweis der Prüfung: {data.review_note}</p>}
        {data.status === "MISSING" && data.data?.requested_at && (
          <p className="mt-1 font-normal" data-testid="tax-requested-hint">KLEDIO hat dich um diese Angaben gebeten — bitte fülle das Formular unten aus.</p>
        )}
      </div>
      {data.reporting_hint && (
        <div className="border border-amber-300 bg-amber-50 rounded-2xl p-4 text-sm text-amber-800 flex gap-2.5" data-testid="tax-reporting-hint">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{rules.hint}</span>
        </div>
      )}
      <div className={card} data-testid="tax-year-stats">
        <p className="font-bold text-sm mb-3">Deine Verkaufsaktivität {data.year}</p>
        <div className="flex flex-wrap gap-6 text-sm">
          <span><b className="text-lg">{data.stats.sales_count}</b><br /><span className="text-xs text-muted-foreground">Verkäufe</span></span>
          <span><b className="text-lg">{formatPrice(data.stats.gross_revenue)}</b><br /><span className="text-xs text-muted-foreground">Bruttoerlöse</span></span>
          <span><b className="text-lg">{formatPrice(data.stats.payouts)}</b><br /><span className="text-xs text-muted-foreground">Ausgezahlt</span></span>
          <span><b className="text-lg">{formatPrice(data.stats.refunds)}</b><br /><span className="text-xs text-muted-foreground">Erstattungen</span></span>
        </div>
      </div>
      <div className={card}>
        <p className="font-bold text-sm mb-1 flex items-center gap-2"><FileText className="w-4 h-4 text-accent" /> Steuerangaben ({rules.name} — {rules.legal_basis})</p>
        <p className="text-xs text-muted-foreground mb-4">{rules.hint}</p>
        <div className="grid sm:grid-cols-2 gap-3.5">
          <div><label className={lb}>Land</label>
            <select data-testid="tax-country-select" className={inp} value={f.country} onChange={setV("country")}>
              <option value="DE">Deutschland</option><option value="AT">Österreich</option><option value="CH">Schweiz</option>
            </select></div>
          <div><label className={lb}>Kontotyp</label>
            <select data-testid="tax-type-select" className={inp} value={f.account_type} onChange={setV("account_type")}>
              <option value="PRIVATE">Privat</option><option value="BUSINESS">Gewerblich</option>
            </select></div>
          <div><label className={lb}>Vorname</label><input data-testid="tax-firstname" className={inp} value={f.first_name} onChange={setV("first_name")} /></div>
          <div><label className={lb}>Nachname</label><input data-testid="tax-lastname" className={inp} value={f.last_name} onChange={setV("last_name")} /></div>
          {f.account_type === "PRIVATE" && (
            <div><label className={lb}>Geburtsdatum {rules.birth_date_required ? "" : "(optional)"}</label>
              <input data-testid="tax-birthdate" type="date" className={inp} value={f.birth_date} onChange={setV("birth_date")} /></div>
          )}
          {f.account_type === "BUSINESS" && (
            <>
              <div><label className={lb}>Firmenname</label><input data-testid="tax-company" className={inp} value={f.company_name} onChange={setV("company_name")} /></div>
              <div><label className={lb}>USt-IdNr. (optional)</label><input data-testid="tax-vatid" className={inp} value={f.vat_id} onChange={setV("vat_id")} placeholder="DE123456789" /></div>
              <div><label className={lb}>Handelsregister-Nr. (optional)</label><input data-testid="tax-regno" className={inp} value={f.business_reg_no} onChange={setV("business_reg_no")} /></div>
            </>
          )}
          <div className="sm:col-span-2"><label className={lb}>Straße & Hausnummer</label><input data-testid="tax-street" className={inp} value={f.street} onChange={setV("street")} /></div>
          <div><label className={lb}>PLZ</label><input data-testid="tax-zip" className={inp} value={f.zip_code} onChange={setV("zip_code")} /></div>
          <div><label className={lb}>Ort</label><input data-testid="tax-city" className={inp} value={f.city} onChange={setV("city")} /></div>
          <div className="sm:col-span-2"><label className={lb}>{rules.tin_label}{rules.tin_required ? "" : " (optional)"}</label>
            <input data-testid="tax-tin" className={inp} value={f.tin} onChange={setV("tin")} placeholder={f.country === "DE" ? "11-stellige IdNr" : ""} /></div>
        </div>
        <button data-testid="tax-save-btn" disabled={busy} onClick={save} className={`${btnPrimary} mt-5`}>
          {busy ? "Wird gespeichert …" : "Steuerangaben speichern"}
        </button>
        <p className="text-[11px] text-muted-foreground mt-3">{data.disclaimer}</p>
      </div>
    </div>
  );
}

function WerbungSection() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/ads/my").then(({ data }) => setData(data)).catch(() => setData(false)); }, []);
  const activeBoosts = (data && data !== false && data.boosts?.filter((b) => b.status === "ACTIVE").length) || 0;
  const activeCampaigns = (data && data !== false && data.campaigns?.filter((c) => c.status === "ACTIVE").length) || 0;
  return (
    <div className="space-y-4">
      <div className={card} data-testid="settings-werbung-card">
        <p className="font-bold text-sm mb-2 flex items-center gap-2"><Rocket className="w-4 h-4 text-accent" /> Werbung & Boosts</p>
        <p className="text-sm text-muted-foreground">Gib deinen Artikeln mehr Sichtbarkeit — ab 1,99 €. Geboostete Artikel erscheinen weiter oben in Suche und Startseite.</p>
        {data && data !== false && (
          <div className="flex flex-wrap gap-4 mt-3">
            <span className="text-sm bg-secondary rounded-full px-3.5 py-1.5" data-testid="active-boosts-count">Aktive Boosts: <b>{activeBoosts}</b></span>
            <span className="text-sm bg-secondary rounded-full px-3.5 py-1.5" data-testid="active-campaigns-count">Aktive Kampagnen: <b>{activeCampaigns}</b></span>
          </div>
        )}
        <button data-testid="settings-goto-werbung" onClick={() => nav("/werbung")} className={`${btnPrimary} mt-4`}>Zum Werbebereich</button>
      </div>
      <InfoCard title="So funktionieren Boosts" text="Du wählst Laufzeit und Artikel, bezahlst sicher über Stripe und dein Artikel wird sofort prominent platziert. Keine Abos, keine versteckten Kosten." />
    </div>
  );
}

const EMAIL_NOTIFS = [
  ["new_message", "Neue Nachricht"], ["new_offer", "Neues Angebot"], ["offer_accepted", "Angebot angenommen"],
  ["item_sold", "Artikel verkauft"], ["payment", "Zahlung erhalten"], ["shipping", "Versandstatus"],
  ["payout", "Auszahlung"], ["review", "Bewertungen"], ["security", "Sicherheitswarnungen"],
];
const PUSH_NOTIFS = [["chat", "Chat"], ["offers", "Angebote"], ["sales", "Verkäufe"], ["payouts", "Auszahlungen"], ["favorites", "Favoriten"]];

function BenachrichtigungenSection({ user, refresh }) {
  const [ns, setNs] = useState(user.notification_settings || {});
  const [ps, setPs] = useState(user.push_settings || { enabled: false });
  const [save] = useSaver(refresh);
  return (
    <div className="space-y-4">
      <div className={card}>
        <p className="font-bold text-sm mb-1">E-Mail-Benachrichtigungen</p>
        {EMAIL_NOTIFS.map(([k, label]) => (
          <Row key={k} label={label} testid={`notif-row-${k}`}>
            <Switch data-testid={`notif-switch-${k}`} checked={ns[k] !== false}
              onCheckedChange={(v) => { const next = { ...ns, [k]: v }; setNs(next); save({ notification_settings: next }); }} />
          </Row>
        ))}
      </div>
      <div className={card}>
        <p className="font-bold text-sm mb-1">Push-Benachrichtigungen</p>
        <p className="text-xs text-muted-foreground mb-2">Für die kommende KLEDIO-App. Deine Auswahl wird bereits gespeichert.</p>
        <Row label="Push-Benachrichtigungen" testid="push-master-row">
          <Switch data-testid="push-master-switch" checked={!!ps.enabled}
            onCheckedChange={(v) => { const next = { ...ps, enabled: v }; setPs(next); save({ push_settings: next }); }} />
        </Row>
        {PUSH_NOTIFS.map(([k, label]) => (
          <Row key={k} label={label} testid={`push-row-${k}`}>
            <Switch data-testid={`push-switch-${k}`} disabled={!ps.enabled} checked={!!ps.enabled && ps[k] !== false}
              onCheckedChange={(v) => { const next = { ...ps, [k]: v }; setPs(next); save({ push_settings: next }); }} />
          </Row>
        ))}
      </div>
    </div>
  );
}

function DatenschutzSection({ user, refresh }) {
  const [p, setP] = useState({ profile_visible: true, show_online: true, show_last_active: true, show_city: true, message_permission: "ALL", ...(user.privacy_settings || {}) });
  const [save] = useSaver(refresh);
  const upd = (k, v) => { const next = { ...p, [k]: v }; setP(next); save({ privacy_settings: next }); };
  const exportData = async () => {
    try {
      const { data } = await api.get("/auth/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "kledio-datenexport.json";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Datenexport heruntergeladen");
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-4">
      <div className={card}>
        <p className="font-bold text-sm mb-1">Privatsphäre</p>
        <Row label="Profil sichtbar" testid="privacy-profile-row"><Switch data-testid="privacy-profile-switch" checked={p.profile_visible !== false} onCheckedChange={(v) => upd("profile_visible", v)} /></Row>
        <Row label="Online-Status anzeigen" testid="privacy-online-row"><Switch data-testid="privacy-online-switch" checked={p.show_online !== false} onCheckedChange={(v) => upd("show_online", v)} /></Row>
        <Row label="Letzte Aktivität anzeigen" testid="privacy-lastactive-row"><Switch data-testid="privacy-lastactive-switch" checked={p.show_last_active !== false} onCheckedChange={(v) => upd("show_last_active", v)} /></Row>
        <Row label="Stadt im Profil anzeigen" testid="privacy-city-row"><Switch data-testid="privacy-city-switch" checked={p.show_city !== false} onCheckedChange={(v) => upd("show_city", v)} /></Row>
        <div className="pt-3">
          <label className={lbl}>Wer darf mir Nachrichten senden?</label>
          <Select value={p.message_permission || "ALL"} onValueChange={(v) => upd("message_permission", v)}>
            <SelectTrigger data-testid="privacy-messages-select" className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Alle</SelectItem>
              <SelectItem value="BUYERS">Nur Käufer meiner Artikel</SelectItem>
              <SelectItem value="NONE">Niemand</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <InfoCard title="Deine sensiblen Daten bleiben privat" text="E-Mail, Telefonnummer, Adresse, Bank-/KYC-Daten und interne Bewertungen sind niemals öffentlich sichtbar." />
      <div className={card}>
        <p className="font-bold text-sm mb-2 flex items-center gap-2"><Cookie className="w-4 h-4 text-accent" /> Cookie-Einstellungen</p>
        <p className="text-sm text-muted-foreground mb-3">Verwalte deine Einwilligungen für Komfort- und Statistik-Cookies.</p>
        <button data-testid="open-cookie-settings" onClick={() => window.dispatchEvent(new Event("kledio:cookie-settings"))} className={btnSecondary}>Cookie-Einstellungen öffnen</button>
      </div>
      <div className={card}>
        <p className="font-bold text-sm mb-2 flex items-center gap-2"><Download className="w-4 h-4 text-accent" /> Meine Daten</p>
        <p className="text-sm text-muted-foreground mb-3">Lade eine Kopie deiner bei KLEDIO gespeicherten Daten herunter (Profil, Artikel, Bestellungen, Nachrichten, Favoriten, Bewertungen).</p>
        <button data-testid="export-data-btn" onClick={exportData} className={btnSecondary}>Meine Daten herunterladen</button>
      </div>
    </div>
  );
}

function VerifizierungSection({ user }) {
  const seller = user.seller || {};
  const items = [
    ["E-Mail", user.email_verified, user.email_verified ? "Verifiziert" : "Nicht verifiziert — Link im Posteingang"],
    ["Telefon", !!user.phone_verified, user.phone_verified ? "Verifiziert" : "Nicht verifiziert"],
    ["Verkäufer (Stripe)", !!seller.payouts_enabled, seller.payouts_enabled ? "Stripe-Verifizierung abgeschlossen" : seller.onboarding_status === "IN_PROGRESS" ? "In Bearbeitung" : "Nicht gestartet"],
    ["Identität (KYC)", seller.verification_status === "VERIFIED", seller.verification_status === "VERIFIED" ? "Bestätigt" : "Ausstehend"],
  ];
  return (
    <div className={card} data-testid="verification-card">
      <p className="font-bold text-sm mb-1">Verifizierung</p>
      {items.map(([label, ok, desc]) => (
        <Row key={label} label={label} desc={desc} testid={`verify-row-${label}`}>
          <span className={`text-lg ${ok ? "text-green-600" : "text-muted-foreground"}`}>{ok ? "✓" : "○"}</span>
        </Row>
      ))}
    </div>
  );
}

function BadgesSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/seller/badges").then(({ data }) => setData(data)).catch(() => setData(false)); }, []);
  if (data === null) return <div className="h-40 bg-muted rounded-2xl animate-pulse" />;
  if (!data) return <div className={card}><p className="text-sm text-muted-foreground">Abzeichen sind für Verkäufer verfügbar. Stelle deinen ersten Artikel ein!</p></div>;
  const earned = data.earned || [];
  const progress = data.progress || [];
  return (
    <div className="space-y-4">
      <div className={card}>
        <p className="font-bold text-sm mb-3">Meine Abzeichen</p>
        {earned.length === 0 ? <p className="text-sm text-muted-foreground">Noch keine Abzeichen verdient.</p> : (
          <div className="flex flex-wrap gap-2" data-testid="earned-badges">{earned.map((b) => <BadgeChip key={b.key} badge={b} />)}</div>
        )}
      </div>
      {progress.length > 0 && (
        <div className={card} data-testid="badge-progress">
          <p className="font-bold text-sm mb-3">Fortschritt zum nächsten Abzeichen</p>
          <div className="space-y-5">
            {progress.map((p) => (
              <div key={p.key}>
                <p className="text-sm font-bold">{p.icon} {p.name}</p>
                <div className="mt-1.5 space-y-1.5">
                  {p.items?.map((it) => {
                    const pct = it.target ? Math.min(100, Math.round((it.current / it.target) * 100)) : 0;
                    return (
                      <div key={it.label}>
                        <div className="flex justify-between text-xs"><span className="text-muted-foreground">{it.label}</span><span className="font-semibold">{it.type === "bool" ? (it.current ? "✓" : "—") : `${it.current} / ${it.target}`}</span></div>
                        <div className="h-1.5 bg-secondary rounded-full mt-1 overflow-hidden">
                          <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SpracheSection({ user, refresh }) {
  const [save] = useSaver(refresh);
  return (
    <div className={card}>
      <p className="font-bold text-sm mb-4">Sprache & Region</p>
      <div className="space-y-4">
        <div><label className={lbl}>Sprache</label>
          <Select value={user.language || "de"} onValueChange={(v) => save({ language: v })}>
            <SelectTrigger data-testid="language-select" className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="de">Deutsch</SelectItem>
              <SelectItem value="en" disabled>English (bald verfügbar)</SelectItem>
              <SelectItem value="fr" disabled>Français (bald verfügbar)</SelectItem>
              <SelectItem value="es" disabled>Español (bald verfügbar)</SelectItem>
              <SelectItem value="it" disabled>Italiano (bald verfügbar)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><label className={lbl}>Land</label>
          <Select value={user.country || "Deutschland"} onValueChange={(v) => save({ country: v })}>
            <SelectTrigger data-testid="region-country-select" className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Deutschland">Deutschland</SelectItem><SelectItem value="Österreich">Österreich</SelectItem><SelectItem value="Schweiz">Schweiz</SelectItem></SelectContent>
          </Select>
        </div>
        <div><label className={lbl}>Währung</label>
          <Select value={user.currency || "EUR"} onValueChange={(v) => save({ currency: v })}>
            <SelectTrigger data-testid="currency-select" className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="EUR">EUR €</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function HilfeSection() {
  const links = [
    ["Hilfe-Center", "/hilfe"], ["Käuferschutz", "/kaeuferschutz"], ["Gebühren", "/gebuehren"],
    ["Verkäuferbedingungen", "/verkaeuferbedingungen"], ["Sicherheit", "/sicherheit"], ["Meldestelle", "/meldestelle"],
    ["AGB", "/agb"], ["Datenschutz", "/datenschutz"],
  ];
  return (
    <div className={card}>
      <p className="font-bold text-sm mb-2">Hilfe</p>
      {links.map(([label, to]) => (
        <Link key={to} to={to} data-testid={`help-link-${to.slice(1)}`} className="flex items-center justify-between py-3 border-b border-border/60 last:border-0 text-sm font-medium hover:text-accent transition-colors">
          {label} <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </Link>
      ))}
    </div>
  );
}

function LoeschenSection() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { password });
      setUser(null);
      toast.success("Dein Konto wurde gelöscht.");
      nav("/");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-red-200 bg-red-50/40 rounded-2xl p-5" data-testid="delete-account-card">
      <p className="font-bold text-sm text-red-700">Konto löschen</p>
      <p className="text-sm text-muted-foreground mt-1.5">Das Löschen deines Kontos kann nicht rückgängig gemacht werden. Deine aktiven Artikel werden entfernt und dein Profil anonymisiert. Bei offenen Bestellungen oder Auszahlungen ist die Löschung erst nach Abschluss möglich.</p>
      <button data-testid="delete-account-btn" onClick={() => setOpen(true)} className="mt-4 bg-red-600 text-white text-sm font-bold px-6 py-2.5 rounded-full hover:opacity-90">Konto löschen</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Konto endgültig löschen?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Bitte bestätige die Löschung mit deinem Passwort. Diese Aktion kann nicht rückgängig gemacht werden.</p>
          <input data-testid="delete-password-input" className={input} type="password" placeholder="Passwort" value={password} onChange={(e) => setPassword(e.target.value)} />
          <div className="flex gap-2">
            <button data-testid="delete-cancel-btn" onClick={() => setOpen(false)} className={`${btnSecondary} flex-1`}>Abbrechen</button>
            <button data-testid="delete-confirm-btn" onClick={del} disabled={!password || busy} className="flex-1 bg-red-600 text-white text-sm font-bold py-2.5 rounded-full hover:opacity-90 disabled:opacity-50">Endgültig löschen</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================= Page ================= */

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const { section } = useParams();
  const nav = useNavigate();
  if (!user) return null;

  const active = SECTIONS.find((s) => s.id === section);
  const content = (id) => {
    switch (id) {
      case "profil": return <ProfilSection user={user} refresh={refresh} />;
      case "konto": return <KontoSection user={user} refresh={refresh} />;
      case "sicherheit": return <SicherheitSection />;
      case "versand": return <VersandSection user={user} refresh={refresh} />;
      case "zahlungen": return <ZahlungenSection user={user} />;
      case "verkaeufer": return <VerkaeuferSection user={user} refresh={refresh} />;
      case "steuern": return <SteuernSection />;
      case "werbung": return <WerbungSection />;
      case "benachrichtigungen": return <BenachrichtigungenSection user={user} refresh={refresh} />;
      case "datenschutz": return <DatenschutzSection user={user} refresh={refresh} />;
      case "verifizierung": return <VerifizierungSection user={user} />;
      case "abzeichen": return <BadgesSection />;
      case "sprache": return <SpracheSection user={user} refresh={refresh} />;
      case "hilfe": return <HilfeSection />;
      case "loeschen": return <LoeschenSection />;
      default: return <ProfilSection user={user} refresh={refresh} />;
    }
  };

  return (
    <div className="bg-[#FAFAF8] md:min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        {/* ===== Desktop ===== */}
        <div className="hidden md:flex gap-10">
          <aside className="w-[260px] shrink-0" data-testid="settings-sidebar">
            <h1 className="text-2xl font-bold mb-5 px-3">Einstellungen</h1>
            <nav className="space-y-0.5">
              {SECTIONS.filter((s) => !s.hidden).map((s) => (
                <button key={s.id} data-testid={`settings-nav-${s.id}`} onClick={() => nav(`/konto/${s.id}`)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-colors ${(active?.id || "profil") === s.id ? "bg-white border border-border shadow-sm text-foreground" : s.danger ? "text-red-600 hover:bg-red-50" : "text-foreground/70 hover:bg-white/70"}`}>
                  <s.icon className={`w-4 h-4 ${(active?.id || "profil") === s.id ? "text-accent" : ""}`} />
                  {s.label}
                </button>
              ))}
            </nav>
          </aside>
          <main className="flex-1 max-w-[860px]" data-testid="settings-content">
            <h2 className="text-xl font-bold mb-5">{active?.label || "Profil"}</h2>
            {content(active?.id || "profil")}
          </main>
        </div>

        {/* ===== Mobile ===== */}
        <div className="md:hidden">
          {!active ? (
            <div data-testid="settings-mobile-menu">
              <h1 className="text-lg font-bold mb-3">Profil</h1>

              <div className="bg-white border border-border rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="profile-hub-card">
                <div className="flex items-center gap-3">
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover border border-border shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-base font-bold shrink-0">{user.username?.[0]?.toUpperCase()}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate" data-testid="hub-name">{user.name || user.username}</p>
                    <p className="text-xs text-muted-foreground truncate">@{user.username}</p>
                  </div>
                  <span data-testid="hub-payout-status" className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 ${user.seller?.payouts_enabled ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                    {user.seller?.payouts_enabled ? "Verifiziert" : "Nicht verifiziert"}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/60 text-xs text-muted-foreground" data-testid="hub-stats">
                  <span><b className="text-foreground">{user.stats?.sales_count || 0}</b> Verkäufe</span>
                  <span><b className="text-foreground">{user.stats?.purchases_count || 0}</b> Käufe</span>
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    <b className="text-foreground">{user.stats?.rating_count ? (user.stats.rating_sum / user.stats.rating_count).toFixed(1) : "–"}</b>
                  </span>
                </div>
                <Link to={`/profil/${user.id}`} data-testid="hub-public-profile-link"
                  className="mt-2.5 flex items-center justify-between text-xs font-semibold text-accent">
                  Öffentliches Profil ansehen <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div className="bg-white border border-border rounded-2xl overflow-hidden mt-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="profile-quicklinks">
                {QUICK_LINKS.map((q) => (
                  <Link key={q.to} to={q.to} data-testid={`quicklink-${q.to.slice(1)}`}
                    className="flex items-center gap-3 px-3.5 py-2.5 text-sm font-medium border-b border-border/50 last:border-0 active:bg-secondary transition-colors">
                    <q.icon className="w-4 h-4 text-accent" />
                    <span className="flex-1">{q.label}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                  </Link>
                ))}
              </div>

              <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mt-4 mb-1.5 px-1">Einstellungen</p>
              <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                {SECTIONS.filter((s) => !s.hidden).map((s) => (
                  <button key={s.id} data-testid={`mobile-settings-nav-${s.id}`} onClick={() => nav(`/konto/${s.id}`)}
                    className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-sm font-medium text-left border-b border-border/50 last:border-0 active:bg-secondary transition-colors ${s.danger ? "text-red-600" : ""}`}>
                    <s.icon className={`w-4 h-4 ${s.danger ? "text-red-500" : "text-accent"}`} />
                    <span className="flex-1">{s.label}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                ))}
              </div>

              <button data-testid="mobile-logout-btn"
                onClick={async () => { await logout(); toast("Du wurdest abgemeldet."); nav("/"); }}
                className="w-full mt-3 bg-white border border-border rounded-2xl px-3.5 py-3 text-sm font-bold text-red-600 flex items-center justify-center gap-2 active:bg-secondary transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <LogOut className="w-4 h-4" /> Abmelden
              </button>

              <div className="mt-6 mb-2 text-center" data-testid="profile-hub-footer">
                <div className="bg-primary text-primary-foreground rounded-3xl p-5 relative overflow-hidden text-left">
                  <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-accent/20 blur-2xl" aria-hidden="true" />
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary-foreground/60">KLEDIO · Dein Marktplatz</p>
                  <p className="font-display text-lg font-extrabold mt-1.5 leading-snug">Verkaufen ohne Gebühren.<br />Kaufen mit Schutz.</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <span className="text-[10px] font-semibold bg-white/10 rounded-full px-2.5 py-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-accent" /> Käuferschutz</span>
                    <span className="text-[10px] font-semibold bg-white/10 rounded-full px-2.5 py-1">0 % Verkäufergebühr</span>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-4">
                  <Link to="/agb" className="hover:text-accent">AGB</Link> · <Link to="/datenschutz" className="hover:text-accent">Datenschutz</Link> · <Link to="/impressum" className="hover:text-accent">Impressum</Link> · <Link to="/hilfe" className="hover:text-accent">Hilfe</Link>
                </p>
                <p className="text-[10px] text-muted-foreground/70 mt-1.5">© {new Date().getFullYear()} KLEDIO</p>
              </div>
            </div>
          ) : (
            <div data-testid="settings-mobile-section">
              <button data-testid="settings-mobile-back" onClick={() => nav("/konto")} className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-3 -ml-1 p-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Profil
              </button>
              <h1 className="text-lg font-bold mb-3">{active.label}</h1>
              {content(active.id)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
